import { useQuery } from '@tanstack/react-query'
import { useApiFetch } from '../../shared/api'

interface SeasonStatsResponse {
  huntsAttended: number
  hoursInField: number
  gameReported: number
  downed: number
  observed: number
  wounded: number
  seasonStart: string
  seasonEnd: string
}

export interface ChronicleSeasonStats {
  huntsAttended: number
  hoursInField: number
  gameReported: number
  downed: number
  observed: number
  wounded: number
  isLoading: boolean
  /**
   * The request failed. Reported separately from the zeros because the two mean
   * opposite things to a hunter: "you logged nothing this season" versus "we could
   * not find out". Callers must render this state rather than the numbers — a
   * failed load previously fell through to all-zero figures with isLoading false,
   * which is indistinguishable from a genuinely empty season.
   */
  isError: boolean
  /** Retries the query, so an error state can offer a way out. */
  retry: () => void
}

const EMPTY = {
  huntsAttended: 0,
  hoursInField: 0,
  gameReported: 0,
  downed: 0,
  observed: 0,
  wounded: 0,
}

export function useChronicleSeasonStats(): ChronicleSeasonStats {
  const call = useApiFetch()

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['chronicle', 'seasonStats'],
    queryFn: () => call<SeasonStatsResponse>('/chronicle/me/season-stats'),
    staleTime: 5 * 60 * 1000,
  })

  const retry = () => {
    void refetch()
  }

  if (!data) {
    return { ...EMPTY, isLoading: isPending, isError, retry }
  }

  return {
    huntsAttended: data.huntsAttended,
    hoursInField: data.hoursInField,
    gameReported: data.gameReported,
    downed: data.downed,
    observed: data.observed,
    // Tolerated as absent so a web build can deploy ahead of the API that adds
    // the field, rather than rendering NaN in the breakdown for a few minutes.
    wounded: data.wounded ?? 0,
    isLoading: false,
    isError: false,
    retry,
  }
}
